import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { MenuItem, TextField } from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import { observer } from 'mobx-react';
import format from 'string-template';
import * as Sentry from '@sentry/browser';

import DialogRoundedModal from '../../../../../../../../components/DialogRoundedModal';
import { CancelButton, SaveButton } from '../../../../../../../../components/Button/ActionButtons';
import InputLabel from '../../../../../../../../components/v2/InputLabel';
import Select from '../../../../../../../../components/v2/Select';
import UserContract from '../../../../../../../../Model/UserContract';
import useComponentMounted from '../../../../../../../../hooks/useComponentMounted';
import { formatCurrencyCents } from '../../../../../../../../utils/formatters';
import { formatPluralDuration } from '../../../../../../../../utils/durationUnit';
import RadioButtonGroup from '../../../../../../../components/RadioButtonGroup';
import LoadingOverlay from '../../../../../../../components/LoadingOverlay';
import useToast from '../../../../../../../hooks/useToast';
import {
  CATEGORY_CONFIG,
  OfferingType,
  formatAcceptUntil,
  getChallengeStart,
  isChallengeStillAccepting,
} from '../../../../../../../components/ContractModal/PackageCategoryCard/utils';
import ConfirmDialog from '../../../../../../../components/ConfirmDialog';
import assignOfferingCategory, { CATEGORY_REASSIGNED } from './utils';
import {
  AcceptUntilChip,
  FieldContainer,
  HelperText,
  OptionChip,
  OptionChips,
  OptionName,
  OptionRow,
  StyledAutocomplete,
} from './styles';
import texts from './texts.json';

// Short enough that the open list stays inside the modal instead of overflowing it.
const MAX_LIST_HEIGHT = 260;

const OFFERING_TYPE_OPTIONS = [
  { value: OfferingType.PACKAGE, label: texts.package },
  { value: OfferingType.CHALLENGE, label: texts.challenge },
];

/**
 * Assigns one offering to an Inside Sales category.
 *
 * Every category takes a package, chosen product-first so the package list stays short. Low Ticket
 * also accepts a challenge, which is picked from the coach's challenge contracts — the same source
 * the Direct Purchase Contract picker reads.
 */
const AssignOfferingModal = ({
  open,
  row,
  products,
  coachId,
  onClose,
  onAssigned,
}) => {
  const currentOffering = row.offering;
  const currentPackage = currentOffering?.type === OfferingType.PACKAGE ? currentOffering.doc : null;
  const currentChallenge = currentOffering?.type === OfferingType.CHALLENGE ? currentOffering.doc : null;
  const [offeringType, setOfferingType] = useState(currentOffering?.type || OfferingType.PACKAGE);
  // Opened on what the category already holds, so Update shows the current pick rather than blanks.
  const [selectedProductId, setSelectedProductId] = useState(currentPackage?.productId || '');
  const [selectedPackageId, setSelectedPackageId] = useState(currentPackage?.id || '');
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [packages, setPackages] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();

  const isAssigningChallenge = row.canHoldChallenge && offeringType === OfferingType.CHALLENGE;

  // A challenge lives on a product of its own, so only standard products can fill a category.
  const selectableProducts = useMemo(
    () => products.filter((product) => !product.isArchived && !product.isChallenge),
    [products],
  );

  useEffect(() => {
    if (!row.canHoldChallenge) {
      return undefined;
    }
    let isStale = false;
    const loadChallenges = async () => {
      setIsLoading(true);
      try {
        const collection = await UserContract.getBaseContractsByCoach(coachId);
        /*
         * A closed challenge cannot be sold, so it is not on offer — except the one this category
         * already holds, which stays listed so Update opens on it rather than on nothing.
         */
        const offerable = collection.docs.filter((contract) => !!contract.challengeId
          && (isChallengeStillAccepting(contract) || contract.id === currentChallenge?.id));
        if (!isStale && isComponentMountedRef.current) {
          setChallenges(offerable);
        }
      } catch (error) {
        Sentry.captureException(error, { extra: { coachId } });
        if (!isStale && isComponentMountedRef.current) {
          showToast(texts.loadError, { error: true });
        }
      } finally {
        if (!isStale && isComponentMountedRef.current) {
          setIsLoading(false);
        }
      }
    };
    loadChallenges();
    return () => { isStale = true; };
  }, [coachId, row.canHoldChallenge, currentChallenge, isComponentMountedRef, showToast]);

  const loadPackagesFor = useCallback(async (productId) => {
    const product = selectableProducts.find(({ id }) => id === productId);
    if (!product) {
      return;
    }
    setIsLoading(true);
    try {
      const collection = await product.getPackages();
      if (isComponentMountedRef.current) {
        setPackages(collection.docs.filter((pkg) => !pkg.isArchived));
      }
    } catch (error) {
      Sentry.captureException(error, { extra: { productId } });
      if (isComponentMountedRef.current) {
        showToast(texts.loadError, { error: true });
      }
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [selectableProducts, isComponentMountedRef, showToast]);

  /*
   * The package the category already holds, loaded as the modal opens. Its product is not being
   * chosen, so this skips the reset a fresh pick does — that would clear the very selection it is
   * here to show.
   */
  useEffect(() => {
    if (currentPackage?.productId) {
      loadPackagesFor(currentPackage.productId);
    }
  }, [currentPackage, loadPackagesFor]);

  const handleProductChange = useCallback(async (productId) => {
    setSelectedProductId(productId);
    setSelectedPackageId('');
    setPackages([]);
    await loadPackagesFor(productId);
  }, [loadPackagesFor]);

  // The coach can have a long list of challenges, so the picker is searchable by name and each
  // option carries the figures that tell two similarly named challenges apart.
  const challengeOptions = useMemo(() => challenges.map((contract) => {
    const acceptUntil = formatAcceptUntil(contract.acceptUntil);
    const start = getChallengeStart(contract.startDate);
    return {
      id: contract.id,
      contract,
      name: contract.name,
      price: formatCurrencyCents(contract.initialPaymentInCents, contract.currency, { minimumFractionDigits: 2 }),
      duration: formatPluralDuration(contract.initialTerm, contract.durationUnit),
      start: start && (start.isOngoing ? texts.ongoingChip : format(texts.startsChip, { date: start.date })),
      acceptUntil: acceptUntil ? format(texts.acceptUntilChip, { date: acceptUntil }) : '',
    };
  }), [challenges]);

  // Select the challenge the category holds once its option exists, so Update opens on it.
  useEffect(() => {
    if (!currentChallenge || selectedChallenge) {
      return;
    }
    const current = challengeOptions.find(({ id }) => id === currentChallenge.id);
    if (current) {
      setSelectedChallenge(current);
    }
  }, [currentChallenge, challengeOptions, selectedChallenge]);

  const selectedDoc = isAssigningChallenge
    ? selectedChallenge?.contract
    : packages.find(({ id }) => id === selectedPackageId);

  /*
   * The category an offering already holds, when it is not this one. Neither picker hides an
   * offering that is already assigned, so picking one silently empties the category it came from —
   * the coach is told which before it happens.
   */
  const reassignedFromCategory = selectedDoc?.category && selectedDoc.category !== row.category
    ? selectedDoc.category
    : '';
  const [isConfirmingReassign, setIsConfirmingReassign] = useState(false);

  const handleAssign = useCallback(async () => {
    if (!selectedDoc) {
      return;
    }
    setIsConfirmingReassign(false);
    setIsSaving(true);
    try {
      await assignOfferingCategory({
        category: row.category,
        nextDoc: selectedDoc,
        currentDoc: currentOffering?.doc,
      });
      showToast(texts.assigned, { type: 'success' });
      await onAssigned();
    } catch (error) {
      // Losing the race is someone else's edit landing first, not a fault — report it as such.
      const wasReassigned = error.message === CATEGORY_REASSIGNED;
      if (!wasReassigned) {
        Sentry.captureException(error, { extra: { category: row.category, coachId } });
      }
      if (isComponentMountedRef.current) {
        showToast(wasReassigned ? texts.reassignedError : texts.assignError, { error: true });
      }
    } finally {
      if (isComponentMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [
    selectedDoc,
    row.category,
    currentOffering,
    coachId,
    onAssigned,
    showToast,
    isComponentMountedRef,
  ]);

  // Taking a category off another one is the coach's call to make, so it is asked before saving.
  const requestAssign = useCallback(() => {
    if (reassignedFromCategory) {
      setIsConfirmingReassign(true);
      return;
    }
    handleAssign();
  }, [reassignedFromCategory, handleAssign]);

  return (
    <DialogRoundedModal
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      title={format(texts.title, { category: row.label })}
      description={texts.description}
      actionButtons={(
        <>
          <SaveButton
            disabled={!selectedDoc || isSaving}
            icon={<CheckCircleOutline />}
            onClick={requestAssign}
          >
            {texts.assign}
          </SaveButton>
          <CancelButton onClick={onClose}>{texts.cancel}</CancelButton>
        </>
      )}
    >
      {row.canHoldChallenge && (
        <FieldContainer>
          <InputLabel>{texts.offeringType}</InputLabel>
          <RadioButtonGroup
            isRow
            options={OFFERING_TYPE_OPTIONS}
            selectedOption={offeringType}
            onOptionChange={setOfferingType}
          />
          <HelperText>{texts.typeHelper}</HelperText>
        </FieldContainer>
      )}

      {isAssigningChallenge ? (
        <FieldContainer>
          <InputLabel>{texts.challengeLabel}</InputLabel>
          <StyledAutocomplete
            value={selectedChallenge}
            options={challengeOptions}
            // Keep the list below the field: flipping it up covers the form it belongs to.
            componentsProps={{ popper: { placement: 'bottom-start', modifiers: [{ name: 'flip', enabled: false }] } }}
            ListboxProps={{ style: { maxHeight: MAX_LIST_HEIGHT } }}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label={texts.selectChallenge} />}
            onChange={(event, value) => setSelectedChallenge(value)}
            renderOption={(optionProps, option) => (
              <OptionRow {...optionProps} key={option.id}>
                <OptionName>{option.name}</OptionName>
                <OptionChips>
                  <OptionChip label={option.price} size="small" />
                  <OptionChip label={option.duration} size="small" />
                  {!!option.start && <OptionChip label={option.start} size="small" />}
                  {!!option.acceptUntil && <AcceptUntilChip label={option.acceptUntil} size="small" />}
                </OptionChips>
              </OptionRow>
            )}
          />
          <HelperText>{challenges.length ? texts.challengeHelper : texts.noChallenges}</HelperText>
        </FieldContainer>
      ) : (
        <>
          <FieldContainer>
            <InputLabel>{texts.product}</InputLabel>
            <Select
              displayEmpty
              value={selectedProductId}
              onChange={(event) => handleProductChange(event.target.value)}
            >
              <MenuItem value="" disabled>{texts.selectProduct}</MenuItem>
              {selectableProducts.map((product) => (
                <MenuItem key={product.id} value={product.id}>{product.name}</MenuItem>
              ))}
            </Select>
          </FieldContainer>
          <FieldContainer>
            <InputLabel>{texts.packageLabel}</InputLabel>
            <Select
              displayEmpty
              disabled={!selectedProductId}
              value={selectedPackageId}
              onChange={(event) => setSelectedPackageId(event.target.value)}
            >
              <MenuItem value="" disabled>{texts.selectPackage}</MenuItem>
              {packages.map((pkg) => (
                <MenuItem key={pkg.id} value={pkg.id}>{pkg.name}</MenuItem>
              ))}
            </Select>
            <HelperText>
              {(selectedProductId && !packages.length) ? texts.noPackages : texts.packageHelper}
            </HelperText>
          </FieldContainer>
        </>
      )}

      <ConfirmDialog
        isOpen={isConfirmingReassign}
        onConfirm={handleAssign}
        onCancel={() => setIsConfirmingReassign(false)}
        dialogTexts={{
          title: texts.reassignConfirm.title,
          content: format(texts.reassignConfirm.content, {
            offering: selectedDoc?.name ?? '',
            from: CATEGORY_CONFIG[reassignedFromCategory]?.label ?? reassignedFromCategory,
            to: row.label,
          }),
          confirm: texts.reassignConfirm.confirm,
        }}
      />

      <LoadingOverlay isLoading={isLoading || isSaving} />
    </DialogRoundedModal>
  );
};

AssignOfferingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  row: PropTypes.object.isRequired,
  products: PropTypes.array.isRequired,
  coachId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onAssigned: PropTypes.func.isRequired,
};

export default observer(AssignOfferingModal);
